// Generated from the validated Extreme scene plan: one block per shot, in order,
// following the camera.
// Each shot's section is scroll room; its card is mounted in the scene beside the part the camera shows
// (the stage publishes that part's screen position as --xl-ax/--xl-ay and its box as --xl-bx0..--xl-by1),
// with a leader line to the part, so the copy and the 3D read as one thing rather than a side column.
import React, { useEffect, useState } from 'react';
import { SHOTS, MOTION, layoutFor, zoneFor } from './LandingShots';

/** The headline figure of a numeral shot: the first number (with its unit) in its fact or title. */
function figureOf(callout) {
  const text = `${callout.fact || ''} ${callout.title || ''}`;
  const match = text.match(/\d[\d.,]*\s?(?:[A-Za-z%]{1,4}\b)?/);
  return match ? match[0].trim() : '';
}

/** What the page hands in, made renderable: a plain object ({ number, title }, 12 Oct 2026) shows its text
 * instead of crashing the whole page. */
function asNode(value) {
  if (value === null || value === undefined || typeof value !== 'object' || React.isValidElement(value)) return value;
  if (Array.isArray(value)) return React.Children.toArray(value.map(asNode));
  return Object.values(value).filter((v) => typeof v === 'string' || typeof v === 'number').join(' · ');
}

/** A numeral shot's title that only repeats its giant figure ("85 Nm" under "85 Nm") is left out. */
function repeatsFigure(title, figure) {
  const bare = (text) => String(text || '').replace(/\s+/g, '').toLowerCase();
  return Boolean(figure) && bare(title) === bare(figure);
}

const WEAVE_CSS = `
:root { --xl-ax: 70vw; --xl-ay: 50vh; }
.xl-shot { position: relative; min-height: 110vh; }
.xl-shot.xl-hero { min-height: 120vh; }
/* The card owns its zone (LAYOUT, per viewport); the 3D stage frames the subject in the rest of the screen.
   Locked with !important so page styles can never move, enlarge or stack the cards. */
.xl-shot > .xl-card {
  position: fixed !important; z-index: 3; box-sizing: border-box;
  width: var(--xl-card-w) !important; max-width: none !important; left: var(--xl-card-inset) !important;
  right: auto !important; top: 50% !important; bottom: auto !important; transform: translateY(-50%) !important;
  max-height: var(--xl-card-h) !important; overflow: auto !important; margin: 0 !important;
  opacity: 0 !important; visibility: hidden !important; transition: opacity 360ms ease, visibility 0s linear 360ms;
}
.xl-shot[data-side='right'] > .xl-card {
  left: calc(100vw - var(--xl-card-w) - var(--xl-card-inset)) !important;
}
.xl-shot.is-active > .xl-card { opacity: 1 !important; visibility: visible !important; transition: opacity 360ms ease; }
/* fitCards() steps a crowded card's content down a little (--xl-fit, never below 80%) instead of hiding its end. */
.xl-shot > .xl-card > * { zoom: var(--xl-fit, 1); }
/* A card is an annotation, not a poster: type is capped to its zone. */
.xl-shot > .xl-card :is(h1, h2, .xl-title) {
  font-size: clamp(20px, 2.1vw, 34px) !important; line-height: 1.08 !important; overflow-wrap: anywhere;
}
.xl-shot.xl-hero > .xl-card h1 { font-size: clamp(26px, 3vw, 46px) !important; }
.xl-shot > .xl-card :is(p, li, .xl-body, .xl-fact, .xl-value) {
  font-size: clamp(13px, 0.95vw, 16px) !important; line-height: 1.5 !important;
}
/* Template signatures inside a card (the SRD's spec rows, code panel, chips): compact, never wider than the card. */
.xl-shot > .xl-card :is(dt, .xl-chip) { font-size: clamp(10px, 0.75vw, 13px) !important; }
.xl-shot > .xl-card dd { font-size: clamp(14px, 1.3vw, 22px) !important; font-variant-numeric: tabular-nums; }
.xl-shot > .xl-card .xl-code {
  font-size: clamp(11px, 0.8vw, 14px) !important; white-space: pre-wrap; overflow-wrap: anywhere; margin: 10px 0 0;
}
.xl-shot > .xl-card .xl-specs { margin: 10px 0 0; }
.xl-shot > .xl-card .xl-specs > div { display: flex; justify-content: space-between; gap: 12px; }
.xl-shot > .xl-card .xl-stack-line { display: block; }
.xl-shot.xl-hero > .xl-card .xl-stack-line {
  font-size: clamp(30px, 4.6vw, 80px) !important; line-height: 0.95 !important;
}
/* The 3D stage paints over the page's ground and under its copy, however the page CSS positions and fills .xl-page
   (12 Oct 2026: a positioned .xl-page with a solid ground, drawn after the stage, hid every object). The copy
   wrappers stay see-through so the stage shows between the cards. */
.extreme-stage { z-index: 1 !important; }
.xl-copy { position: relative !important; z-index: 2 !important; }
.xl-copy, .xl-shot { background: none !important; }
/* No band, strip or ticker across the page: a full-width striped chapter band with a sliding spec ticker broke the
   scroll through the scene (14 Oct 2026, user). One a page still renders stays hidden. */
.xl-divider, .xl-ticker, .xl-marquee, marquee { display: none !important; }
.xl-shot > .xl-lead {
  position: fixed; z-index: 2; pointer-events: none; height: 1px; top: clamp(15vh, var(--xl-ay), 85vh);
  left: calc(var(--xl-card-inset) + var(--xl-card-w));
  width: max(0px, calc(var(--xl-ax) - var(--xl-card-inset) - var(--xl-card-w)));
  background: currentColor; opacity: 0; transition: opacity 360ms ease;
}
.xl-shot > .xl-lead::after {
  content: ''; position: absolute; right: -4px; top: -4px; width: 9px; height: 9px; border-radius: 50%;
  border: 1px solid currentColor;
}
.xl-shot[data-side='right'] > .xl-lead {
  left: var(--xl-ax); width: max(0px, calc(100vw - var(--xl-card-w) - var(--xl-card-inset) - var(--xl-ax)));
}
.xl-shot[data-side='right'] > .xl-lead::after { right: auto; left: -4px; }
.xl-shot.is-active > .xl-lead { opacity: 0.7; }
/* Floating labels pinned to the pieces of a labelled shot (the stage publishes --xl-l<k>x/y): translucent chips on
   the product itself, by design. Their look comes from the page CSS; placement and the fade are fixed here. */
.xl-shot > .xl-label {
  position: fixed; z-index: 3; pointer-events: none; box-sizing: border-box; max-width: 220px;
  left: clamp(8px, var(--xl-lx, -999px), calc(100vw - 230px)); top: clamp(72px, var(--xl-ly, -999px), calc(100vh - 32px));
  transform: translate(16px, -50%); padding: 6px 10px; border-radius: 999px; white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; font-size: clamp(11px, 0.8vw, 13px) !important; line-height: 1.3;
  background: rgba(255, 255, 255, 0.16); border: 1px solid rgba(255, 255, 255, 0.4); backdrop-filter: blur(6px);
  opacity: 0; visibility: hidden; transition: opacity 360ms ease, visibility 0s linear 360ms;
}
.xl-shot > .xl-label::before {
  content: ''; position: absolute; left: 0; top: 50%; width: 8px; height: 8px; margin: -4px 0 0 -16px;
  border-radius: 50%; background: currentColor;
}
.xl-shot.is-active > .xl-label { opacity: 1; visibility: visible; transition: opacity 360ms ease 160ms; }
/* Compositions (desktop and tablet): each shot is staged differently, so the page is not one repeated diagram. */
@media (min-width: 768px) {
  .xl-shot:not([data-composition='annotate']) > .xl-lead { display: none; }
  .xl-shot[data-composition='statement'] > .xl-card {
    left: 8vw !important; width: 84vw !important; top: 11vh !important; transform: none !important;
    max-height: 36vh !important; text-align: center; background: transparent !important; box-shadow: none !important;
    border: 0 !important;
  }
  .xl-shot[data-composition='statement'] > .xl-card :is(h1, h2, .xl-title) {
    font-size: clamp(40px, 6.2vw, 104px) !important; line-height: 0.98 !important; letter-spacing: -0.02em;
  }
  .xl-shot[data-composition='fullbleed'] > .xl-card {
    left: var(--xl-card-inset) !important; width: min(26vw, 380px) !important; top: auto !important;
    bottom: 7vh !important; transform: none !important;
  }
  .xl-shot[data-composition='split'] > .xl-card :is(h1, h2, .xl-title) {
    font-size: clamp(28px, 3.6vw, 60px) !important; line-height: 1.02 !important;
  }
  .xl-shot[data-composition='numeral'] > .xl-card .xl-figure {
    display: block; margin: 0 0 8px; font-size: clamp(72px, 10vw, 168px) !important; line-height: 0.9 !important;
    font-weight: 700; letter-spacing: -0.04em; font-variant-numeric: tabular-nums;
  }
  /* A full-height pane on its side: a chapter's longer read, edge to edge, the product beside it. */
  .xl-shot[data-composition='pane'] > .xl-card {
    left: 0 !important; top: 0 !important; transform: none !important; height: 100vh !important;
    max-height: none !important; border-radius: 0 !important; display: flex; flex-direction: column;
    justify-content: center; padding: clamp(88px, 12vh, 128px) clamp(28px, 4vw, 72px) clamp(32px, 6vh, 64px) !important;
  }
  .xl-shot[data-composition='pane'][data-side='right'] > .xl-card { left: calc(100vw - var(--xl-card-w)) !important; }
  .xl-shot[data-composition='pane'] > .xl-card :is(h1, h2, .xl-title) {
    font-size: clamp(28px, 3.4vw, 56px) !important; line-height: 1.04 !important;
  }
  /* A labelled shot: the labels do the talking; its card is a quiet caption at the bottom. */
  .xl-shot[data-composition='labels'] > .xl-card {
    left: var(--xl-card-inset) !important; width: min(28vw, 380px) !important; top: auto !important;
    bottom: 7vh !important; transform: none !important;
  }
}
.xl-figure { display: none; }
/* A card's chapter number: shown only by the card styles that use it (extreme_variety). */
.xl-index { display: none; }
@media (max-width: 767px) {
  .xl-shot[data-composition='numeral'] > .xl-card .xl-figure {
    display: block; margin: 0 0 4px; font-size: clamp(48px, 15vw, 80px) !important; line-height: 0.95 !important;
    font-weight: 700;
  }
  .xl-shot[data-composition='statement'] > .xl-card :is(h1, h2, .xl-title) {
    font-size: clamp(30px, 9vw, 44px) !important; line-height: 1 !important;
  }
}
@media (max-width: 767px) {
  /* Phone: the stage is a band at the top; the card sits under it, full width. */
  .xl-shot > .xl-card, .xl-shot[data-side='right'] > .xl-card {
    left: 16px !important; width: calc(100vw - 32px) !important; top: calc(var(--xl-band) + 12px) !important;
    transform: none !important; max-height: calc(100vh - var(--xl-band) - 28px) !important;
  }
  .xl-shot > .xl-lead, .xl-shot[data-side='right'] > .xl-lead {
    width: 1px; left: var(--xl-ax); top: var(--xl-ay);
    height: max(0px, calc(var(--xl-band) + 12px - var(--xl-ay)));
  }
  .xl-shot > .xl-lead::after, .xl-shot[data-side='right'] > .xl-lead::after { top: -4px; left: -4px; right: auto; }
  .xl-shot > .xl-label { top: clamp(60px, var(--xl-ly, -999px), calc(var(--xl-band) - 20px)); max-width: 46vw; }
}
@media (prefers-reduced-motion: reduce) {
  .xl-shot > .xl-card, .xl-shot > .xl-lead { transition: none; }
}
`;
// How this page's cards and labels enter, in its motion feel (extreme_motion.feel_css).
const FEEL_CSS = `
/* Motion feel: Bounce - toy-box delight, springy and lively. */
.xl-shot > .xl-card { transition: opacity 200ms ease, visibility 0s linear 200ms; }
.xl-shot.is-active > .xl-card { transition: opacity 240ms ease-out; }
.xl-shot > .xl-label { transition: opacity 200ms ease, translate 200ms ease, scale 200ms ease, visibility 0s linear 200ms; }
.xl-shot.is-active > .xl-label { transition: opacity 480ms cubic-bezier(.34,1.56,.64,1) calc(120ms + var(--xl-k, 0) * 90ms), translate 480ms cubic-bezier(.34,1.56,.64,1) calc(120ms + var(--xl-k, 0) * 90ms), scale 480ms cubic-bezier(.34,1.56,.64,1) calc(120ms + var(--xl-k, 0) * 90ms); }
.xl-shot:not(.is-active) > .xl-label { scale: 0.5; }
:where(.xl-shot) > :where(.xl-card) > * { transition: translate 560ms cubic-bezier(.34,1.56,.64,1), scale 560ms cubic-bezier(.34,1.56,.64,1), opacity 448ms ease; }
:where(.xl-shot:not(.is-active)) > :where(.xl-card) > * { scale: 0.86; opacity: 0; }
:where(.xl-shot.is-active) > :where(.xl-card) > :nth-child(2) { transition-delay: 70ms; }
:where(.xl-shot.is-active) > :where(.xl-card) > :nth-child(3) { transition-delay: 140ms; }
:where(.xl-shot.is-active) > :where(.xl-card) > :nth-child(4) { transition-delay: 210ms; }
:where(.xl-shot.is-active) > :where(.xl-card) > :nth-child(5) { transition-delay: 280ms; }
:where(.xl-shot.is-active) > :where(.xl-card) > :nth-child(6) { transition-delay: 350ms; }
@media (prefers-reduced-motion: reduce) { :where(.xl-shot) > :where(.xl-card) > *, .xl-shot > .xl-label { transition: none !important; translate: none !important; scale: none !important; } }
`;
// The structure of this generation's card styles (extreme_variety): each shot's card is its own kind of object.
const CARD_CSS = `
.xl-shot[data-card-style='glass'] > .xl-card { border-radius: 28px !important; backdrop-filter: blur(16px) saturate(140%); -webkit-backdrop-filter: blur(16px) saturate(140%); }
.xl-shot[data-card-style='inverse'] > .xl-card { border-radius: 0 !important; }
.xl-shot[data-card-style='stamp'] > .xl-card { border: 3px solid currentColor !important; border-radius: 3px !important; }
.xl-shot[data-card-style='stamp'] > .xl-card .xl-eyebrow { display: inline-block; rotate: -3deg; border: 2px solid currentColor; padding: 3px 10px; text-transform: uppercase; letter-spacing: 0.16em; font-weight: 700; }
.xl-shot[data-card-style='stamp'] > .xl-card :is(h1, h2, .xl-title) { text-transform: uppercase; font-stretch: condensed; }
.xl-shot[data-card-style='tag'] > .xl-card { border-radius: 16px !important; rotate: -1.5deg; padding-top: 34px !important; }
.xl-shot[data-card-style='tag'] > .xl-card::before { content: ''; position: absolute; top: 14px; left: 50%; width: 14px; height: 14px; margin-left: -7px; border-radius: 50%; border: 2px solid currentColor; opacity: 0.7; }
.xl-shot[data-card-style='tag'][data-side='right'] > .xl-card { rotate: 1.5deg; }
`;

/** The widest word of an element's own text, unbroken (a hyphenated word may break at its hyphen). */
function widestWord(el) {
  let widest = 0;
  el.childNodes.forEach((node) => {
    if (node.nodeType !== 3) return;
    for (const match of node.textContent.matchAll(/[^\s-]{3,}/g)) {
      const range = document.createRange();
      range.setStart(node, match.index);
      range.setEnd(node, match.index + match[0].length);
      widest = Math.max(widest, range.getBoundingClientRect().width);
    }
  });
  return widest;
}

/** How far the card's last line of text runs below what the card shows (its own bottom spacing does not count). */
function hiddenText(card) {
  const edge = card.getBoundingClientRect().top + card.clientTop + card.clientHeight;
  let lastLine = edge;
  const walker = document.createTreeWalker(card, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.textContent.trim()) continue;
    const range = document.createRange();
    range.selectNodeContents(node);
    for (const rect of range.getClientRects()) lastLine = Math.max(lastLine, rect.bottom);
  }
  return lastLine - edge;
}

/** Fit every card's copy into its fixed box (12 Oct 2026: cards hid up to 232 px of copy and broke "AURION" across
 * two lines). Type whose longest word is wider than the card gets smaller instead of breaking the word, then a card
 * that still overflows steps its content down (never below 80%) instead of hiding its last lines. Load and resize
 * only - never on scroll. */
function fitCards() {
  document.querySelectorAll('.xl-shot > .xl-card').forEach((card) => {
    card.style.setProperty('--xl-fit', '1');
    card.querySelectorAll('[data-xl-fit]').forEach((el) => {
      el.style.removeProperty('font-size');
      el.removeAttribute('data-xl-fit');
    });
    const box = getComputedStyle(card);
    const room = card.clientWidth - parseFloat(box.paddingLeft) - parseFloat(box.paddingRight) - 4;
    if (room <= 0) return;
    card.querySelectorAll('*').forEach((el) => {
      if (![...el.childNodes].some((node) => node.nodeType === 3 && node.textContent.trim())) return;
      el.style.whiteSpace = 'nowrap';
      const widest = widestWord(el);
      el.style.whiteSpace = '';
      if (widest > room) {
        const size = parseFloat(getComputedStyle(el).fontSize);
        el.style.setProperty('font-size', `${Math.max(11, Math.floor((size * room) / widest))}px`, 'important');
        el.setAttribute('data-xl-fit', '');
      }
    });
    for (let fit = 0.96; fit > 0.79 && hiddenText(card) > 2; fit -= 0.04) {
      card.style.setProperty('--xl-fit', fit.toFixed(2));
    }
  });
}

/** The planned zones for this viewport, as the CSS variables the cards use. */
function layoutVars() {
  const zone = layoutFor(typeof window === 'undefined' ? 1440 : window.innerWidth);
  return `:root { --xl-card-w: ${zone.cardWidth}; --xl-card-inset: ${zone.inset}; ` +
    `--xl-card-h: ${zone.cardMaxHeight}; --xl-band: ${zone.band}; }`;
}

export default function LandingShotList({ body = {}, before = null, after = null }) {
  const [active, setActive] = useState(SHOTS[0] ? SHOTS[0].id : null);

  const [vars, setVars] = useState(layoutVars);
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  // A card shows only while a shot block holds the middle of the screen: past the shot list (a plain section, the
  // footer) none stays pinned over the page (12 Oct 2026: the closing card sat under a repair-made hero wordmark).
  const [inside, setInside] = useState(true);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const crossing = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.isIntersecting ? crossing.add(entry.target) : crossing.delete(entry.target)));
      setInside(crossing.size > 0);
    }, { rootMargin: '-50% 0px -50% 0px' });
    document.querySelectorAll('.xl-shot').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let live = true;
    const frame = requestAnimationFrame(fitCards);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => live && fitCards());
    return () => {
      live = false;
      cancelAnimationFrame(frame);
    };
  }, [vars, width]);

  useEffect(() => {
    const onShot = (event) => setActive(event.detail && event.detail.id);
    const onResize = () => {
      setVars(layoutVars());
      setWidth(window.innerWidth);
    };
    window.addEventListener('xl-shot', onShot);
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('xl-shot', onShot);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <>
      <style>{WEAVE_CSS + FEEL_CSS + CARD_CSS + vars}</style>
      {SHOTS.map((shot, index) => {
        const callout = shot.callout || {};
        const state = inside && shot.id === active ? ' is-active' : '';
        const edge = index === 0 ? ' xl-hero' : index === SHOTS.length - 1 ? ' xl-closing' : '';
        return (
          <section
            key={shot.id}
            className={`xl-shot${state}${edge}`}
            id={shot.id}
            data-shot={shot.id}
            data-section={shot.section || ''}
            style={{ '--xl-card-w': zoneFor(width, shot).cardWidth }}
            data-side={shot.card === 'right' ? 'right' : 'left'}
            data-composition={shot.composition || 'annotate'}
            data-feel={MOTION.feel}
            data-card-style={shot.card_style || undefined}
            data-staging={shot.staging && shot.staging !== 'standard' ? shot.staging : undefined}
          >
            <span className="xl-lead" aria-hidden="true" />
            {(shot.labels || []).map((label, k) => (
              <span className="xl-label" key={label.anchor}
                style={{ '--xl-lx': `var(--xl-l${k}x)`, '--xl-ly': `var(--xl-l${k}y)`, '--xl-k': k }}>{label.text}</span>
            ))}
            <div className="xl-card">
              {index === 0 ? asNode(before) : null}
              <span className="xl-index" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              {shot.eyebrow ? <p className="xl-eyebrow">{shot.eyebrow}</p> : null}
              {shot.composition === 'numeral' && figureOf(callout) ? <p className="xl-figure">{figureOf(callout)}</p> : null}
              {callout.title && !((index === 0 && before) || (index === SHOTS.length - 1 && after))
                && !(shot.composition === 'numeral' && repeatsFigure(callout.title, figureOf(callout)))
                ? <h2 className="xl-title">{callout.title}</h2> : null}
              {callout.fact ? <p className="xl-fact">{callout.fact}</p> : null}
              {callout.value ? <p className="xl-value">{callout.value}</p> : null}
              {body[shot.id] ? <div className="xl-body">{asNode(body[shot.id])}</div> : null}
              {index === SHOTS.length - 1 ? asNode(after) : null}
            </div>
          </section>
        );
      })}
    </>
  );
}
